import { useState } from 'react'
import { ApiError } from '@akillidernek/api-client'
import { Button, Card, CardBody, CardHeader, Field, Input } from '@akillidernek/ui'
import { api } from '../api'

/**
 * E-postadaki bağlantıların indiği ekranlar — YES-157.
 *
 * Bu yollar oturum AÇILMADAN önce çalışır; router'ın (AppShell) dışında,
 * `main.tsx` tarafından seçilirler. Üç yol var ve üçü de tek kullanımlık bir
 * jeton taşır: jeton tüketilir tüketilmez adres çubuğundan silinir
 * (`history.replaceState`) — geri tuşuna basan biri onu bir daha
 * kullanamaz, tarayıcı geçmişinde de kalmaz.
 */
export type JetonYolu = 'magic' | 'sifre' | 'davet'

export function jetonYolunuCoz(): { yol: JetonYolu; token: string } | null {
  const { pathname, search } = window.location
  const token = new URLSearchParams(search).get('token')
  if (!token) return null
  if (pathname === '/giris/baglanti') return { yol: 'magic', token }
  if (pathname === '/sifre-sifirla') return { yol: 'sifre', token }
  if (pathname === '/davet') return { yol: 'davet', token }
  return null
}

export function adresiTemizle() {
  window.history.replaceState(null, '', '/')
}

/** Şifre sıfırlama ve davet. Magic link `Giris`'in kendisinde (main.tsx). */
export function JetonEkrani({ yol, token }: { yol: Exclude<JetonYolu, 'magic'>; token: string }) {
  if (yol === 'sifre') return <SifreSifirla token={token} />
  return <DavetKabul token={token} />
}

function Kabuk({
  title,
  description,
  children,
}: {
  title: string
  description: string
  children: React.ReactNode
}) {
  return (
    <div className="grid min-h-dvh place-items-center p-6">
      <Card className="w-[min(26rem,100%)]">
        <CardHeader title={title} description={description} />
        <CardBody>{children}</CardBody>
      </Card>
    </div>
  )
}

function SifreSifirla({ token }: { token: string }) {
  const [hata, setHata] = useState<string | null>(null)
  const [alanHatalari, setAlanHatalari] = useState<Record<string, string>>({})
  const [gonderiliyor, setGonderiliyor] = useState(false)
  const [bitti, setBitti] = useState(false)

  async function gonder(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    const sifre = String(form.get('password') ?? '')
    if (sifre !== String(form.get('password2') ?? '')) {
      setAlanHatalari({ password2: 'İki şifre aynı değil.' })
      return
    }
    setGonderiliyor(true)
    setHata(null)
    setAlanHatalari({})
    try {
      await api.POST('/api/v1/auth/password-reset/confirm', { body: { token, password: sifre } })
      adresiTemizle()
      setBitti(true)
    } catch (error) {
      if (error instanceof ApiError) {
        setHata(error.problem.detail)
        setAlanHatalari(error.fieldErrors())
      } else setHata('Beklenmeyen bir hata oluştu.')
    } finally {
      setGonderiliyor(false)
    }
  }

  if (bitti) {
    return (
      <Kabuk title="Şifreniz değişti" description="Diğer cihazlardaki oturumlarınız kapatıldı.">
        {/* Sıfırlama çoğu zaman "hesabıma başkası giriyor" şüphesiyle yapılır;
            eski oturumların düştüğünü söylemek o şüpheye cevap. */}
        <Button onClick={() => window.location.assign('/')}>Giriş yap</Button>
      </Kabuk>
    )
  }

  return (
    <Kabuk
      title="Yeni şifre belirle"
      description="En az 12 karakter. Uzunluk, karmaşıklıktan daha iyi bir ölçüt."
    >
      <form onSubmit={gonder} className="flex flex-col gap-3">
        <Field label="Yeni şifre" required error={alanHatalari.password}>
          {(props) => (
            <Input
              name="password"
              type="password"
              autoComplete="new-password"
              minLength={12}
              autoFocus
              {...props}
            />
          )}
        </Field>
        <Field label="Yeni şifre (tekrar)" required error={alanHatalari.password2}>
          {(props) => (
            <Input
              name="password2"
              type="password"
              autoComplete="new-password"
              minLength={12}
              {...props}
            />
          )}
        </Field>
        {hata && (
          <p role="alert" className="text-xs text-[var(--ad-danger)]">
            {hata}
          </p>
        )}
        <Button type="submit" disabled={gonderiliyor}>
          {gonderiliyor ? 'Kaydediliyor…' : 'Şifreyi değiştir'}
        </Button>
      </form>
    </Kabuk>
  )
}

function DavetKabul({ token }: { token: string }) {
  const [hata, setHata] = useState<string | null>(null)
  const [alanHatalari, setAlanHatalari] = useState<Record<string, string>>({})
  const [gonderiliyor, setGonderiliyor] = useState(false)
  const [dernek, setDernek] = useState<string | null>(null)

  async function gonder(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const sifre = String(new FormData(event.currentTarget).get('password') ?? '')
    setGonderiliyor(true)
    setHata(null)
    setAlanHatalari({})
    try {
      const { data } = await api.POST('/api/v1/invitations/accept', {
        // Boş şifre = "zaten şifrem var". Sunucu None bekliyor, boş dize değil.
        body: { token, password: sifre || undefined },
      })
      adresiTemizle()
      setDernek(data?.name ?? 'Dernek')
    } catch (error) {
      if (error instanceof ApiError) {
        setHata(error.problem.detail)
        setAlanHatalari(error.fieldErrors())
      } else setHata('Beklenmeyen bir hata oluştu.')
    } finally {
      setGonderiliyor(false)
    }
  }

  if (dernek) {
    return (
      <Kabuk title="Davet kabul edildi" description={`${dernek} yönetim paneline eklendiniz.`}>
        {/* Kabul oturum açmaz (sunucu kararı): yeni belirlenen şifre bir kez
            daha kullanılsın ki doğru kaydedildiği görülsün. */}
        <Button onClick={() => window.location.assign('/')}>Giriş yap</Button>
      </Kabuk>
    )
  }

  return (
    <Kabuk
      title="Daveti kabul et"
      description="Şifrenizi siz belirlersiniz; davet eden kişi şifrenizi görmez."
    >
      <form onSubmit={gonder} className="flex flex-col gap-3">
        <Field
          label="Şifre"
          error={alanHatalari.password}
          hint="Akıllı Dernek'te zaten hesabınız varsa boş bırakın; mevcut şifreniz geçerli kalır."
        >
          {(props) => (
            <Input
              name="password"
              type="password"
              autoComplete="new-password"
              minLength={12}
              autoFocus
              {...props}
            />
          )}
        </Field>
        {hata && (
          <p role="alert" className="text-xs text-[var(--ad-danger)]">
            {hata}
          </p>
        )}
        <Button type="submit" disabled={gonderiliyor}>
          {gonderiliyor ? 'Kabul ediliyor…' : 'Daveti kabul et'}
        </Button>
      </form>
    </Kabuk>
  )
}

/** Giriş ekranından "şifremi unuttum" — jeton yolu değil ama aynı aile. */
export function SifremiUnuttum({ onGeri }: { onGeri: () => void }) {
  const [gonderildi, setGonderildi] = useState(false)
  const [gonderiliyor, setGonderiliyor] = useState(false)

  async function gonder(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const email = String(new FormData(event.currentTarget).get('email') ?? '')
    setGonderiliyor(true)
    try {
      await api.POST('/api/v1/auth/password-reset', { body: { email } })
    } finally {
      // Yanıt her durumda aynı: kayıtlı olmayan e-posta belli olmasın.
      setGonderildi(true)
      setGonderiliyor(false)
    }
  }

  return (
    <Kabuk title="Şifremi unuttum" description="E-postanıza sıfırlama bağlantısı göndereceğiz.">
      {gonderildi ? (
        <div className="flex flex-col gap-3 text-sm">
          <p>
            Kayıtlı bir hesap varsa bağlantı gönderildi. Gelen kutunuzu (ve spam klasörünü) kontrol
            edin.
          </p>
          <Button variant="secondary" onClick={onGeri}>
            Girişe dön
          </Button>
        </div>
      ) : (
        <form onSubmit={gonder} className="flex flex-col gap-3">
          <Field label="E-posta" required>
            {(props) => (
              <Input name="email" type="email" autoComplete="username" autoFocus {...props} />
            )}
          </Field>
          <Button type="submit" disabled={gonderiliyor}>
            {gonderiliyor ? 'Gönderiliyor…' : 'Bağlantı gönder'}
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={onGeri}>
            Girişe dön
          </Button>
        </form>
      )}
    </Kabuk>
  )
}
